import React, { useState, useEffect } from "react";
import { array, func, object, bool, number, oneOfType } from "prop-types";
import { Row, Col, Button, Modal, Form } from "react-bootstrap";
import Permissions from "components/Permissions";
import Loader from "react-loader-advanced";
import Circle from "better-react-spinkit/dist/Circle";
import cautionIcon from "../../../resources/images/caution-icon.png";

const ShareTestResultsComponent = ({
  users,
  teams,
  candidateIds,
  onGetUsersAndTeams,
  onShareResults,
  changeAction,
  handleModalCancel,
  fromSearch,
  isInsightUser,
  permissionScoreReports,
  showShareResultsLoader,
  fromBulkShareTestResults,
  showWorkStyles,
  hsVersion,
  showManagerGuideTab,
  candidateStatus,
  isEPPCompleted,
}) => {
  const showManagerGuideOption =
    (hsVersion === 5 || showManagerGuideTab) && candidateStatus === 2;

  const [sharedUsersTeams, setSharedUsersTeams] = useState([]);
  const [usersTeamsValid, setUsersTeamsValid] = useState(null);
  const [sharedDocuments, setSharedDocuments] = useState({
    report: false,
    workplaceInsights: false,
    workStyles: false,
    summary: false,
    application: false,
    resume: false,
    documents: false,
    allDocs: false,
    managerGuide: false,
  });

  useEffect(() => {
    if (users.size < 1) {
      onGetUsersAndTeams();
    }
  }, []);

  /* --------- Event Handlers ---------- */

  const handleChangeAction = (isActionDone) => {
    if (fromSearch) {
      handleModalCancel();
    } else {
      changeAction(false, isActionDone);
    }
  };

  const handleShareItemChoice = (e) => {
    const { value } = e.target.dataset;

    if (value === "allDocs") {
      if (!sharedDocuments.allDocs) {
        setSharedDocuments({
          report: permissionScoreReports,
          workplaceInsights: !isInsightUser,
          workStyles: showWorkStyles,
          summary: true,
          application: fromSearch ? true : !isInsightUser,
          resume: true,
          documents: true,
          allDocs: true,
          managerGuide: true,
        });
      } else {
        setSharedDocuments({
          report: false,
          workplaceInsights: false,
          workStyles: false,
          summary: false,
          application: false,
          resume: false,
          documents: false,
          allDocs: false,
          managerGuide: false,
        });
      }
    } else {
      setSharedDocuments({
        ...sharedDocuments,
        [value]: !sharedDocuments[value],
        allDocs: false,
      });
    }
  };

  const handleShareTeamUserChoice = (e) => {
    setSharedUsersTeams(e);
    setUsersTeamsValid(null);
  };

  const handleSubmitShare = () => {
    if (sharedUsersTeams.length < 1) {
      setUsersTeamsValid("error");
    } else {
      const {
        report,
        workplaceInsights,
        workStyles,
        summary,
        application,
        resume,
        documents,
        managerGuide,
      } = sharedDocuments;

      const shareDataObj = {
        userAccountIds: [],
        teamIds: [],
        testTakerIds: candidateIds,
        options: {
          report,
          workplaceInsights,
          workStyles,
          summary,
          application,
          resume,
          documents,
          managerGuide,
        },
      };

      sharedUsersTeams.forEach((userTeam) => {
        const utObj = userTeam.value.split("_");
        if (utObj[1] === "user") {
          shareDataObj.userAccountIds.push(utObj[0]);
        } else {
          shareDataObj.teamIds.push(utObj[0]);
        }
      });

      onShareResults(shareDataObj);

      if (!fromBulkShareTestResults) {
        handleChangeAction(true);
      }
    }
  };

  const showLoader = fromBulkShareTestResults && showShareResultsLoader;

  const showBulkCandidateShareResultsWarning =
    fromBulkShareTestResults && candidateIds.length > 50;

  return (
    <div>
      <Modal.Header closeButton={fromSearch}>
        <h4>Share Test Results</h4>
      </Modal.Header>
      <Modal.Body>
        <Loader
          show={showLoader}
          message={<Circle color="#2dccd3" size={90} />}
          contentBlur={30}
          messageStyle={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
          backgroundStyle={{ backgroundColor: "rgba( 0 0 0 0)" }}
        >
          {showBulkCandidateShareResultsWarning && (
            <Row
              className="p-3 mx-0 mb-3"
              style={{ backgroundColor: "#fff5e5", color: "#b27100" }}
            >
              <Col>
                <div>
                  <span>
                    <img
                      src={cautionIcon}
                      width="16px"
                      alt="warning"
                      className="mr-1"
                      style={{ marginBottom: "2px" }}
                    />
                  </span>
                  You can select up to 50 candidates to perform this bulk
                  action. Please de-select some of the candidates.
                </div>
              </Col>
            </Row>
          )}
          <Row>
            <Col lg={12} md={12} sm={12}>
              <p>Send candidate PDF documents to Criteria users by email.</p>
              <p>
                <strong>Which documents would you like to share?</strong>
              </p>
              <Form.Check
                type="checkbox"
                onChange={handleShareItemChoice}
                checked={sharedDocuments.allDocs}
                data-value="allDocs"
                label="Select All"
                className="form-check-input-style"
                id="selectAll"
              />
              {permissionScoreReports || isInsightUser ? (
                <Form.Check
                  type="checkbox"
                  onChange={handleShareItemChoice}
                  checked={sharedDocuments.summary}
                  data-value="summary"
                  label="Candidate Summary"
                  className="form-check-input-style"
                  id="candidateSummary"
                />
              ) : null}
              {permissionScoreReports ? (
                <Form.Check
                  type="checkbox"
                  onChange={handleShareItemChoice}
                  checked={sharedDocuments.report}
                  data-value="report"
                  label="Score Reports"
                  className="form-check-input-style"
                  id="scoreReports"
                />
              ) : null}
              {/* Note: Search container does not contain workplaceInsights checkbox */}
              {!isInsightUser && !fromSearch && permissionScoreReports ? (
                <Form.Check
                  type="checkbox"
                  onChange={handleShareItemChoice}
                  checked={sharedDocuments.workplaceInsights}
                  data-value="workplaceInsights"
                  label="Workplace Insights"
                  className="form-check-input-style"
                  id="workplaceInsights"
                />
              ) : null}
              {showManagerGuideOption &&
                isEPPCompleted &&
                permissionScoreReports && (
                  <Form.Check
                    type="checkbox"
                    onChange={handleShareItemChoice}
                    checked={sharedDocuments.managerGuide}
                    data-value="managerGuide"
                    label="Manager Guide"
                    className="form-check-input-style"
                    id="managerGuide"
                  />
                )}
              {showWorkStyles && !fromSearch && permissionScoreReports ? (
                <Form.Check
                  type="checkbox"
                  onChange={handleShareItemChoice}
                  checked={sharedDocuments.workStyles}
                  data-value="workStyles"
                  label="Work Styles"
                  className="form-check-input-style"
                  id="workStyles"
                />
              ) : null}
              <Form.Check
                type="checkbox"
                onChange={handleShareItemChoice}
                checked={sharedDocuments.resume}
                data-value="resume"
                label="Resume"
                className="form-check-input-style"
                id="resume"
              />
              {!isInsightUser ? (
                <Form.Check
                  type="checkbox"
                  onChange={handleShareItemChoice}
                  checked={sharedDocuments.application}
                  data-value="application"
                  label="Job Applications"
                  className="form-check-input-style"
                  id="jobApplications"
                />
              ) : null}
              <Form.Check
                type="checkbox"
                onChange={handleShareItemChoice}
                checked={sharedDocuments.documents}
                data-value="documents"
                label="Additional Documents"
                className="form-check-input-style"
                id="additionalDocuments"
              />
              <br />
              <Permissions
                users={users}
                teams={teams}
                onSetUserInput={handleShareTeamUserChoice}
                sharedUsersTeams={sharedUsersTeams}
                fromResultsShare
                usersTeamsValid={usersTeamsValid}
              />
              {usersTeamsValid === "error" ? (
                <p className="manual-override-invalid-feedback">
                  Please select user(s) and/or team(s)
                </p>
              ) : null}
            </Col>
          </Row>
        </Loader>
      </Modal.Body>
      <br />
      <br />
      <Modal.Footer>
        <Button
          onClick={() => handleChangeAction(false)}
          id="cancelShareResults"
          variant="outline-primary"
          className="btn-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmitShare}
          disabled={
            showLoader ||
            showBulkCandidateShareResultsWarning ||
            (!sharedDocuments.summary &&
              !sharedDocuments.report &&
              !sharedDocuments.workplaceInsights &&
              !sharedDocuments.managerGuide &&
              !sharedDocuments.workStyles &&
              !sharedDocuments.resume &&
              !sharedDocuments.application &&
              !sharedDocuments.documents)
          }
          id="shareResults"
        >
          Send
        </Button>
      </Modal.Footer>
    </div>
  );
};

ShareTestResultsComponent.propTypes = {
  users: object,
  teams: object,
  candidateIds: array,
  changeAction: func,
  onGetUsersAndTeams: func,
  onShareResults: func,
  handleModalCancel: func,
  fromSearch: bool,
  isInsightUser: bool.isRequired,
  permissionScoreReports: number,
  showShareResultsLoader: bool,
  fromBulkShareTestResults: bool,
  showWorkStyles: bool,
  hsVersion: number,
  showManagerGuideTab: bool,
  candidateStatus: number,
  isEPPCompleted: oneOfType([bool, object]),
};

export default ShareTestResultsComponent;
